// /stacks/traffic/dev/frontend/src/lib/dates.js
// Minimal date helpers used by Stats & links. Heavier range logic lives in urlState.js.

const pad2 = (n) => String(n).padStart(2, "0");

/** Robust local-time parsing for API-style timestamps.
 * Accepts Date, or "YYYY-MM-DD HH:MM:SS" and treats it as LOCAL time.
 */
function toLocalDate(d) {
  if (d instanceof Date) return d;
  const s = String(d || "");
  if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}$/.test(s)) {
    // Convert to "YYYY-MM-DDTHH:MM:SS" (interpreted as local by browsers)
    return new Date(s.replace(" ", "T"));
  }
  return new Date(s);
}

/** YYYY-MM-DD (local time) */
export function ymd(d) {
  const dt = d instanceof Date ? d : new Date(d);
  const yyyy = dt.getFullYear();
  const MM = pad2(dt.getMonth() + 1);
  const DD = pad2(dt.getDate());
  return `${yyyy}-${MM}-${DD}`;
}

/** First day of the month at 00:00:00 (local time) */
export function firstOfMonth(d = new Date()) {
  const dt = d instanceof Date ? d : new Date(d);
  return new Date(dt.getFullYear(), dt.getMonth(), 1, 0, 0, 0, 0);
}

/** Add n months, clamped to first-of-month (used for month navigation) */
export function addMonths(d, n) {
  const dt = d instanceof Date ? d : new Date(d);
  return new Date(dt.getFullYear(), dt.getMonth() + n, 1, 0, 0, 0, 0);
}

/** Start/end of day (local time). endOfDay is exclusive (next day 00:00). */
export function startOfDay(d) {
  const dt = d instanceof Date ? d : new Date(d);
  return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate(), 0, 0, 0, 0);
}
export function endOfDay(d) {
  const dt = d instanceof Date ? d : new Date(d);
  return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate() + 1, 0, 0, 0, 0);
}

/** Month name → 0..11 (defensive default to 0) */
export function monthIndexFromName(name) {
  const idx = [
    "January","February","March","April","May","June",
    "July","August","September","October","November","December"
  ].findIndex((m) => m.toLowerCase() === String(name || "").toLowerCase());
  return Math.max(0, idx);
}

/** YYYY-MM-DD HH:MM:SS (second precision, local time) */
export function ymdHms(d) {
  const dt = d instanceof Date ? d : new Date(d);
  const HH = pad2(dt.getHours());
  const mm = pad2(dt.getMinutes());
  const ss = pad2(dt.getSeconds());
  return `${ymd(dt)} ${HH}:${mm}:${ss}`;
}

/**
 * Parse "01:00am"/"12:00pm" → 0..23 (used by TodayCard hour cells).
 * Accepts "1am", "01 am", "01:00am", etc. Falls back to 0 on invalid.
 */
export function parseHour12(label) {
  if (typeof label === "number" && Number.isFinite(label)) {
    const n = Math.max(0, Math.min(23, Math.trunc(label)));
    return n;
  }
  const s = String(label || "").trim().toLowerCase();
  const m = s.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)$/i);
  if (!m) return 0;
  let hour = parseInt(m[1], 10);
  const ampm = m[3];
  if (hour < 1 || hour > 12) return 0;
  hour = hour % 12;
  if (ampm === "pm") hour += 12;
  return hour;
}

/** Friendly timestamp for Sightings rows & image headers.
 * Format: M-D-YYYY h:mm:ss AM/PM Dayname (local time)
 * Example: "10-1-2025 10:48:12 PM Wednesday"
 */
export function fmtFullWithDow(d) {
  const dt = toLocalDate(d);
  const month = dt.getMonth() + 1; // no zero-pad
  const day = dt.getDate();        // no zero-pad
  const year = dt.getFullYear();

  let h = dt.getHours();
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12; // 12-hour clock

  const mm = pad2(dt.getMinutes());
  const ss = pad2(dt.getSeconds());
  const dow = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"][dt.getDay()];

  return `${month}-${day}-${year} ${h}:${mm}:${ss} ${ampm} ${dow}`;
}
